import React, { ReactElement, useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import {
  alertError,
  Button,
  ButtonStyle,
  hasRole,
  Role,
  Animate,
  alertMessage,
  IGiftCardResponse,
} from '@one-tree/library'
import Page from '../../hoc/Page'
import Card, { CardSize } from '../../components/page/Card'
import UserSearch from './UserSearch'
import RedeemerSearch from './RedeemerSearch'
import {
  ISelectOrganisationResult,
  useOrganisation,
} from '../../context/OrganisationProvider'
import { RoutePath } from '../../types/Routes'
import { createQueryString } from '../../helpers/DataTransformer'
import CardHeader from '../../components/page/CardHeader'
import CardContent from '../../components/page/CardContent'
import { get, searchGiftCardCode } from '../../helpers/APIHelper'
import { voucherWord } from '../../helpers/FormatHelper'
import useResponsive from '../../helpers/isResponsive'
import { Resource } from '../../types/API'

const StyledCard = styled(Card)`
  .home-button-container {
    display: flex;
    justify-content: flex-end;
  }
  &.card-size-small {
    ${(props): string => props.theme.isMobile && 'width: 330px'}
  }
`

function Home(): ReactElement {
  const location = useLocation<{ switchToOrgId?: number; state: string }>()
  const history = useHistory()
  const {
    organisation,
    organisations,
    organisationUser,
    selectOrganisation,
    setOrganisationLoading,
  } = useOrganisation()

  useEffect(() => {
    if (location.state?.switchToOrgId) {
      selectOrganisation(location.state.switchToOrgId)
    }
  }, [])

  const [purchaserName, setPurchaserName] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [orderId, setOrderId] = useState('')
  const [voucherId, setVoucherId] = useState('')
  const [voucherCode, setVoucherCode] = useState('')

  // Clear the fields on org change
  useEffect(() => {
    setPurchaserName('')
    setRecipientName('')
    setOrderId('')
    setVoucherId('')
    setVoucherCode('')
  }, [organisation?.id])

  const hasUserRole = organisationUser && hasRole(organisationUser.role, Role.OrganisationUser)

  // If result is from a different org, switch to that first
  const switchHandler = async (
    organisationId: number,
  ): Promise<ISelectOrganisationResult | null> => {
    const switchOrg = organisations.filter(({ id }) => id === organisationId)[0]
      .name

    // This is set to `false` in Page.tsx when warnBeforeExit is used to avoid loading forever
    setOrganisationLoading(true)
    // Clear any data for this org, then reinstate the org list so we can switch to other orgs
    sessionStorage.clear()
    sessionStorage.setItem('organisations', JSON.stringify(organisations))
    const result = await selectOrganisation(organisationId)

    if (result) {
      alertMessage(`Switched to ${switchOrg}`)
      return result
    }

    alertError(`Error switching to ${switchOrg}`)
    return null
  }

  const handleGiftCardNavigation = async (
    giftCard: IGiftCardResponse,
  ): Promise<void> => {
    let currentUser = organisationUser

    // If this gift card is from a different org that we have access to, switch to it.
    // (back end handles "we have access to" part, gives 'Gift card not found' if no access)
    if (
      giftCard.organisationId
      && giftCard.organisationId !== organisation?.id
    ) {
      const result = await switchHandler(giftCard.organisationId)

      if (result) currentUser = result.organisationUser
    }

    if (currentUser && hasRole(currentUser.role, Role.OrganisationUser)) {
      history.push(`${RoutePath.VoucherDetail}/${giftCard.id}`)
    } else {
      history.push(RoutePath.VoucherRedeem, { giftCard })
    }
  }

  // eslint-disable-next-line no-control-regex
  const sanitizeInput = (input: string): string => input.replace(/[\x00-\x1F\x7F]/g, '').trim()

  const searchGiftCard = async (): Promise<void> => {
    const sanitizedParams = {
      purchaserName: sanitizeInput(purchaserName),
      recipientName: sanitizeInput(recipientName),
      orderId: sanitizeInput(orderId),
    }

    if (
      !Object.values(sanitizedParams).find((param) => param.length > 0)
      && !voucherId
      && !voucherCode
    ) {
      if (hasUserRole) {
        alertError('You must include a search term')
      } else {
        alertError('You must include a 16 character code or a Voucher ID')
      }
    } else if (voucherId) {
      const giftCard = await get<IGiftCardResponse>({
        resource: Resource.GiftCards,
        urlPath: [sanitizeInput(voucherId)],
      })

      if (giftCard && giftCard.id) {
        await handleGiftCardNavigation(giftCard)
      }
    } else if (voucherCode) {
      const stripTrailingHyphens = sanitizeInput(voucherCode).replace(/-+$/, '')
      const giftCard = await searchGiftCardCode({ code: stripTrailingHyphens })

      if (giftCard && giftCard.id) {
        await handleGiftCardNavigation(giftCard)
      }
    } else if (/[A-Za-z]/g.test(sanitizedParams.orderId)) {
      alertError('Order ID must be a number')
    } else {
      history.push(
        `${RoutePath.SearchResults}?${createQueryString(sanitizedParams)}`,
      )
    }
  }

  const responsive = useResponsive()

  return (
    <Animate isAnimated={location.state?.state === 'animate'}>
      <Page>
        <StyledCard cardSize={CardSize.Small} theme={responsive}>
          {hasUserRole ? (
            <UserSearch
              searchGiftCard={searchGiftCard}
              purchaserName={purchaserName}
              setPurchaserName={setPurchaserName}
              recipientName={recipientName}
              setRecipientName={setRecipientName}
              orderId={orderId}
              setOrderId={setOrderId}
              voucherId={voucherId}
              setVoucherId={setVoucherId}
              voucherCode={voucherCode}
              setVoucherCode={setVoucherCode}
            />
          ) : (
            <RedeemerSearch
              voucherId={voucherId}
              setVoucherId={setVoucherId}
              voucherCode={voucherCode}
              setVoucherCode={setVoucherCode}
              searchGiftCard={searchGiftCard}
            />
          )}
        </StyledCard>
        {hasUserRole && (
          <StyledCard cardSize={CardSize.Small} theme={responsive}>
            <CardHeader title={`Sell a ${voucherWord(organisation)}`} />
            <CardContent>
              <p>
                {`Issue a ${voucherWord(
                  organisation,
                )} in-house. You will need to take an off-line card or cash payment.`}
              </p>
              <div className="home-button-container">
                <Button
                  buttonStyle={ButtonStyle.Action}
                  onClick={(): void => history.push(RoutePath.ManualOrder)}
                >
                  Manual order
                </Button>
              </div>
            </CardContent>
          </StyledCard>
        )}
      </Page>
    </Animate>
  )
}

export default Home
